import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import styles from './PDFStyles';
import { PDFDocumentProps } from './types';

const SubmissionPDFDocument: React.FC<PDFDocumentProps> = ({ 
  submissionData, 
  isTeacherView,
  generatedAt 
}) => {
  // Format date helper
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate total marks
  const calculateTotalMarks = (): number => {
    return submissionData.submissions.reduce((sum, submission) => {
      return sum + (submission.questionId?.marks || submission.marks || 0);
    }, 0);
  };

  // Calculate optimal chunking based on page space
  const calculateOptimalChunking = (submissions: any[]) => {
    // A4 page height is approximately 842px
    const pageHeight = 842;
    const headerHeight = 120; // Approximate header height on first page
    const titleHeight = 30; // Title height
    const rowHeight = 190; // 180px + 10px margin
    const padding = 20; // Top/bottom padding
    
    // Calculate available space for first page (with header) and subsequent pages
    const firstPageAvailableHeight = pageHeight - headerHeight - titleHeight - padding;
    const subsequentPageAvailableHeight = pageHeight - titleHeight - padding;
    
    // Calculate how many rows fit on each page type
    const rowsOnFirstPage = Math.floor(firstPageAvailableHeight / rowHeight);
    const rowsOnSubsequentPages = Math.floor(subsequentPageAvailableHeight / rowHeight);
    
    // Each row has 2 questions
    const questionsOnFirstPage = rowsOnFirstPage * 2;
    const questionsOnSubsequentPages = rowsOnSubsequentPages * 2;
    
    const chunks = [];
    let remainingSubmissions = [...submissions];
    let isFirstPage = true;
    
    while (remainingSubmissions.length > 0) {
      const questionsPerPage = isFirstPage ? questionsOnFirstPage : questionsOnSubsequentPages;
      const chunk = remainingSubmissions.splice(0, questionsPerPage);
      chunks.push(chunk);
      isFirstPage = false;
    }
    
    return chunks;
  };

  // SnapGrade logo URL (same as email template)
  const logoUrl = 'https://i.imgur.com/Hm96JgT.png';

  // Group submissions with optimal chunking
  const submissionChunks = calculateOptimalChunking(submissionData.submissions);

  // Render a single question in grid format
  const renderGridQuestion = (submission: any, globalIndex: number) => (
    <View style={styles.gridQuestionCell}>
      {/* Question Header */}
      <View style={styles.gridQuestionHeader}>
        <Text style={styles.gridQuestionTitle}>
          Q{globalIndex + 1}: {submission.questionId?.question || submission.question || 'Question text not available'}
        </Text>
        <Text style={styles.gridQuestionScore}>
          {submission.score || 0}/{submission.questionId?.marks || submission.marks || 0}
        </Text>
      </View>

      {/* Content Moderation Warning */}
      {submission.contentModeration?.isInappropriate && (
        <View style={styles.gridFlaggedWarning}>
          <Text style={styles.gridFlaggedText}>
            ⚠️ Content Flagged: {submission.contentModeration.reason}
          </Text>
        </View>
      )}

      {/* Feedback (if graded) */}
      {submissionData.status !== 'pending' && submission.feedback && (
        <View style={styles.gridFeedbackContainer}>
          {submission.feedback.aiComment && (
            <View style={styles.gridFeedbackSection}>
              <Text style={styles.gridFeedbackLabel}>AI Feedback:</Text>
              <Text style={styles.gridFeedbackText}>
                {submission.feedback.aiComment}
              </Text>
            </View>
          )}
          
          {submission.feedback.suggestion && (
            <View style={styles.gridFeedbackSection}>
              <Text style={styles.gridFeedbackLabel}>Improvement:</Text>
              <Text style={styles.gridFeedbackText}>
                {submission.feedback.suggestion}
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );

  return (
    <Document>
      {submissionChunks.map((chunk, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {/* Header - only on first page */}
          {pageIndex === 0 && (
            <View style={styles.compactStructuredHeader}>
              <Image src={logoUrl} style={styles.compactCenteredLogo} />
              
              {/* Right side with submission info */}
              <View style={styles.compactHeaderRightContainer}>
                {/* Submission Information Card */}
                <View style={styles.compactHeaderInfoCard}>
                  <Text style={styles.compactHeaderCardTitle}>Submission Information</Text>
                  
                  {/* Student name */}
                  <View style={styles.compactHeaderInfoRow}>
                    <Text style={styles.compactHeaderInfoLabel}>Student:</Text>
                    <Text style={styles.compactHeaderInfoValue}>
                      {submissionData.student?.name || 'Current Student'}
                    </Text>
                  </View>
                  
                  {/* Class info */}
                  <View style={styles.compactHeaderInfoRow}>
                    <Text style={styles.compactHeaderInfoLabel}>Class:</Text>
                    <Text style={styles.compactHeaderInfoValue}>
                      {submissionData.classId?.name || submissionData.class?.name || 'Unknown Class'}
                    </Text>
                  </View>
                  
                  {/* Submission metadata */}
                  <View style={styles.compactHeaderInfoRow}>
                    <Text style={styles.compactHeaderInfoLabel}>Submitted:</Text>
                    <Text style={styles.compactHeaderInfoValue}>{formatDate(submissionData.submittedAt)}</Text>
                  </View>
                  
                  {/* Total Score */}
                  <View style={styles.compactHeaderInfoRow}>
                    <Text style={styles.compactHeaderInfoLabel}>Total Score:</Text>
                    <Text style={styles.compactHeaderInfoValue}>
                      {submissionData.totalScore || 0} / {calculateTotalMarks()} marks
                    </Text>
                  </View>
                </View>
                
                {/* Generated info in header */}
                <Text style={styles.compactGeneratedInfoHeader}>
                  Generated on {formatDate(generatedAt.toISOString())}
                </Text>
              </View>
            </View>
          )}

          {/* Title */}
          <Text style={styles.compactTitle}>Questions & AI Feedback</Text>
          
          {/* Grid container */}
          <View style={styles.gridContainer}>
            {/* Dynamically render rows based on chunk size */}
            {Array.from({ length: Math.ceil(chunk.length / 2) }, (_, rowIndex) => {
              const startIndex = rowIndex * 2;
              const question1Index = submissionChunks.slice(0, pageIndex).reduce((sum, prevChunk) => sum + prevChunk.length, 0) + startIndex;
              const question2Index = question1Index + 1;
              
              return (
                <View key={rowIndex} style={styles.gridRow}>
                  {chunk[startIndex] && renderGridQuestion(chunk[startIndex], question1Index)}
                  {chunk[startIndex + 1] && renderGridQuestion(chunk[startIndex + 1], question2Index)}
                </View>
              );
            })}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default SubmissionPDFDocument; 